import { useState } from 'react';
import { ArrowLeft, User, Palette, Shield, Database, Trash2, Sun, Moon, Monitor, Type } from 'lucide-react';
import { useUIStore, Theme, FontSize } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useConversationStore } from '../../stores/conversationStore';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '@/lib/utils';
import * as authApi from '../../features/auth/api/authApi';
import * as conversationApi from '../../features/conversations/api/conversationApi';

export function SettingsPage() {
  const { setCurrentView, theme, setTheme, fontSize, setFontSize } = useUIStore();
  const { user, updateUser, logout } = useAuthStore();
  const { loadConversations } = useConversationStore();

  // Profile state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Password state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Delete all chats state
  const [deleteChatsDialogOpen, setDeleteChatsDialogOpen] = useState(false);
  const [deleteChatsLoading, setDeleteChatsLoading] = useState(false);

  // Delete account state
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);

  // Profile save handler
  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);

    try {
      await updateUser({ displayName: displayName || undefined });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (error: any) {
      setProfileError(error.response?.data?.error?.message || 'Fehler beim Speichern');
    } finally {
      setProfileSaving(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async () => {
    setPasswordError(null);

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Das neue Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setPasswordSaving(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Force logout after password change
      await logout();
    } catch (error: any) {
      setPasswordError(error.response?.data?.error?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Delete all chats handler
  const handleDeleteAllChats = async () => {
    setDeleteChatsLoading(true);

    try {
      await conversationApi.deleteAllConversations();
      await loadConversations();
      setDeleteChatsDialogOpen(false);
    } catch (error) {
      console.error('Error deleting chats:', error);
    } finally {
      setDeleteChatsLoading(false);
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    setDeleteAccountError(null);

    if (!deleteAccountPassword) {
      setDeleteAccountError('Bitte geben Sie Ihr Passwort ein');
      return;
    }

    setDeleteAccountLoading(true);

    try {
      await authApi.deleteAccount(deleteAccountPassword);
      await logout();
    } catch (error: any) {
      setDeleteAccountError(error.response?.data?.error?.message || 'Fehler beim Löschen des Kontos');
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Hell', icon: Sun },
    { value: 'dark', label: 'Dunkel', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  const fontSizeOptions: { value: FontSize; label: string }[] = [
    { value: 'small', label: 'Klein' },
    { value: 'medium', label: 'Normal' },
    { value: 'large', label: 'Groß' },
    { value: 'xlarge', label: 'Sehr groß' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentView('chat')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold">Einstellungen</h1>
      </div>

      {/* Settings Content */}
      <ScrollArea className="flex-1">
        <div className="max-w-2xl mx-auto p-6 space-y-6">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profil
              </CardTitle>
              <CardDescription>
                Verwalten Sie Ihre Profilinformationen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Anzeigename</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Ihr Name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                />
                <p className="text-xs text-muted-foreground">
                  Die E-Mail-Adresse kann nicht geändert werden.
                </p>
              </div>
              {profileError && (
                <p className="text-sm text-destructive">{profileError}</p>
              )}
              {profileSuccess && (
                <p className="text-sm text-green-600">Profil erfolgreich gespeichert</p>
              )}
              <Button onClick={handleProfileSave} disabled={profileSaving}>
                {profileSaving ? 'Speichern...' : 'Profil speichern'}
              </Button>
            </CardContent>
          </Card>

          {/* Appearance Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Darstellung
              </CardTitle>
              <CardDescription>
                Passen Sie das Erscheinungsbild der Anwendung an
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Theme</Label>
                <div className="flex gap-2">
                  {themeOptions.map(({ value, label, icon: Icon }) => (
                    <Button
                      key={value}
                      variant={theme === value ? 'default' : 'outline'}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2',
                        theme === value && 'ring-2 ring-primary ring-offset-2'
                      )}
                      onClick={() => setTheme(value)}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  Schriftgröße
                </Label>
                <div className="flex gap-2">
                  {fontSizeOptions.map(({ value, label }) => (
                    <Button
                      key={value}
                      variant={fontSize === value ? 'default' : 'outline'}
                      className={cn(
                        'flex-1',
                        fontSize === value && 'ring-2 ring-primary ring-offset-2'
                      )}
                      onClick={() => setFontSize(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Datenschutz & Sicherheit
              </CardTitle>
              <CardDescription>
                Verwalten Sie Ihre Datenschutz- und Sicherheitseinstellungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                Passwort ändern
              </Button>
            </CardContent>
          </Card>

          {/* Data Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Daten
              </CardTitle>
              <CardDescription>
                Verwalten Sie Ihre Chat-Daten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="destructive"
                onClick={() => setDeleteChatsDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Alle Chats löschen
              </Button>
            </CardContent>
          </Card>

          {/* Account Deletion */}
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Konto löschen
              </CardTitle>
              <CardDescription>
                Löschen Sie Ihr Konto und alle damit verbundenen Daten unwiderruflich.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={() => setDeleteAccountDialogOpen(true)}
              >
                Konto dauerhaft löschen
              </Button>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
            <DialogDescription>
              Geben Sie Ihr aktuelles Passwort und ein neues Passwort ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Mindestens 8 Zeichen
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Neues Passwort bestätigen</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </div>
            {passwordError && (
              <p className="text-sm text-destructive">{passwordError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordDialogOpen(false);
                setPasswordError(null);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
              }}
            >
              Abbrechen
            </Button>
            <Button onClick={handlePasswordChange} disabled={passwordSaving}>
              {passwordSaving ? 'Ändern...' : 'Passwort ändern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Chats Dialog */}
      <Dialog open={deleteChatsDialogOpen} onOpenChange={setDeleteChatsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alle Chats löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie alle Chats löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteChatsDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAllChats}
              disabled={deleteChatsLoading}
            >
              {deleteChatsLoading ? 'Löschen...' : 'Alle Chats löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteAccountDialogOpen} onOpenChange={setDeleteAccountDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konto dauerhaft löschen</DialogTitle>
            <DialogDescription>
              Diese Aktion ist unwiderruflich. Ihr Konto, alle Chats und alle damit verbundenen Daten werden dauerhaft gelöscht.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-4">
            <div className="space-y-2">
              <Label htmlFor="deleteAccountPassword">Passwort zur Bestätigung</Label>
              <Input
                id="deleteAccountPassword"
                type="password"
                value={deleteAccountPassword}
                onChange={(e) => setDeleteAccountPassword(e.target.value)}
                placeholder="Ihr aktuelles Passwort"
              />
            </div>
            {deleteAccountError && (
              <p className="text-sm text-destructive">{deleteAccountError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteAccountDialogOpen(false);
                setDeleteAccountError(null);
                setDeleteAccountPassword('');
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteAccountLoading}
            >
              {deleteAccountLoading ? 'Löschen...' : 'Konto dauerhaft löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
